import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTicketTrace, type TicketTrace, type ConversationTrace } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, AlertCircle, FileSearch, Zap, Search, Brain, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

import { FlowDiagram } from '../workflow-trace/FlowDiagram';
import { PhaseDetails } from '../workflow-trace/PhaseDetails';
import { transformTraceToFlowData, PHASE_CONFIG } from '../workflow-trace/utils';

interface TicketWorkflowTraceProps {
  ticketId: string;
}

// Phase icons mapping
const phaseIcons: Record<string, typeof Zap> = {
  phase1: Zap,
  phase2: Search,
  phase3: FileSearch,
  phase4a: Brain,
  phase4b: Sparkles,
};

// Phase colors mapping
const phaseColors: Record<string, string> = {
  phase1: 'text-blue-500',
  phase2: 'text-green-500',
  phase3: 'text-yellow-500',
  phase4a: 'text-purple-500',
  phase4b: 'text-pink-500',
};

// Convert TicketTrace to ConversationTrace format for reusing existing components
function toConversationTrace(trace: TicketTrace): ConversationTrace {
  return {
    hasTrace: trace.hasTrace,
    conversationId: trace.ticketId,
    message: trace.message,
    executionId: trace.executionId,
    status: trace.status as ConversationTrace['status'],
    createdAt: trace.createdAt,
    summary: trace.summary,
    phase1: trace.phase1,
    phase2: trace.phase2,
    phase3: trace.phase3,
    phase4: trace.phase4,
    error: trace.error,
  };
}

export function TicketWorkflowTrace({ ticketId }: TicketWorkflowTraceProps) {
  const { data: ticketTrace, isLoading, error } = useQuery({
    queryKey: ['ticket-trace', ticketId],
    queryFn: () => getTicketTrace(ticketId),
    enabled: !!ticketId,
  });

  // Convert to ConversationTrace format for reusing existing visualization
  const trace = useMemo(
    () => ticketTrace ? toConversationTrace(ticketTrace) : undefined,
    [ticketTrace]
  );

  // Auto-select first available phase
  const defaultSelectedPhase = useMemo(() => {
    if (trace?.hasTrace && trace.phase1) {
      return 'phase1';
    }
    return null;
  }, [trace]);

  const [selectedPhase, setSelectedPhase] = useState<string | null>(null);

  // Set default selected phase once trace is loaded
  useEffect(() => {
    if (defaultSelectedPhase && !selectedPhase) {
      setSelectedPhase(defaultSelectedPhase);
    }
  }, [defaultSelectedPhase, selectedPhase]);

  // Transform trace data to flow format
  const { nodes, edges } = useMemo(
    () => transformTraceToFlowData(trace, selectedPhase),
    [trace, selectedPhase]
  );

  // Update nodes when selection changes
  const nodesWithSelection = useMemo(
    () => nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        isSelected: node.id === selectedPhase,
      },
    })),
    [nodes, selectedPhase]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[500px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[500px] gap-2">
        <AlertCircle className="h-8 w-8 text-red-500" />
        <p className="text-sm text-muted-foreground">Failed to load trace data</p>
      </div>
    );
  }

  if (!trace?.hasTrace) {
    return (
      <div className="flex flex-col items-center justify-center h-[500px] gap-2">
        <FileSearch className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {ticketTrace?.message || 'No trace data available for this ticket'}
        </p>
      </div>
    );
  }

  // Get selected phase details
  const selectedPhaseConfig = PHASE_CONFIG.find(p => p.key === selectedPhase);
  const SelectedIcon = selectedPhase ? phaseIcons[selectedPhase] : null;

  return (
    <div className="flex h-[600px] gap-4">
      {/* Left side: Flow diagram */}
      <Card className="flex-1 min-w-[300px]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Execution Flow</CardTitle>
        </CardHeader>
        <CardContent className="h-[calc(100%-60px)]">
          <FlowDiagram
            nodes={nodesWithSelection}
            edges={edges}
            onNodeClick={(nodeId) => setSelectedPhase(nodeId)}
          />
        </CardContent>
      </Card>

      {/* Right side: Phase details */}
      <Card className="flex-1 min-w-[400px]">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            {SelectedIcon && selectedPhase && (
              <SelectedIcon className={cn('h-4 w-4', phaseColors[selectedPhase])} />
            )}
            <CardTitle className="text-sm font-medium">
              {selectedPhaseConfig?.name || 'Select a phase'}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="h-[calc(100%-60px)] p-0">
          <ScrollArea className="h-full">
            <div className="p-4">
              {selectedPhase && trace && (
                <PhaseDetails
                  phaseKey={selectedPhase}
                  trace={trace}
                />
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
